/**
 * LLM-9E / Phase 3C — Ask Your Coach panel with genuine multi-turn
 * conversation continuity plus optional memory-suggestion presentation.
 */
import { useCallback, useMemo, useRef, useState } from "react";
import { Loader2 } from "lucide-react";

import {
  askYourCoach,
  createCoachingSession,
  dismissCoachingMemorySuggestion,
  sendCoachingSessionMessage,
} from "@/api/aiCoachApi";
import { CoachingActionCard } from "@/components/coaching/CoachingActionCard";
import { CoachingSectionCard } from "@/components/coaching/CoachingSectionCard";
import { notifyActivePlanChanged } from "@/lib/activePlanSync";
import { MemorySuggestionCard } from "@/components/coaching/MemorySuggestionCard";
import {
  affectsActiveTrainingPlan,
  PROPOSAL_PARSE_STATE,
} from "@/lib/coachingActionGuard";
import { Button } from "@/components/ui/button";

const generateClientMessageId = () => {
  const random = Math.random().toString(16).slice(2, 10).padStart(8, "0");
  const stamp = Date.now().toString(16).slice(-8).padStart(8, "0");
  return `askcoach-${stamp}${random}`;
};

const buildAthleteTurn = (message) => ({
  id: `athlete-${Date.now()}-${Math.random().toString(16).slice(2, 8)}`,
  role: "athlete",
  message,
});

const DEGRADED_NOTICE =
  "AI interpretation is temporarily unavailable; the data shown is from your recorded activities.";

const buildCoachTurn = (response) => ({
  id: `coach-${Date.now()}-${Math.random().toString(16).slice(2, 8)}`,
  role: "coach",
  message: response?.reply || "",
  degradedMode: response?.degradedMode === true,
  retryable: response?.retryable === true,
  availabilityStatus: response?.availabilityStatus || null,
  memorySuggestion:
    response?.memorySuggestion?.observationText
    && response?.memorySuggestion?.promotionCommand
      ? response.memorySuggestion
      : null,
  coachingAction: response?.coachingAction,
  confirmationRequired: response?.confirmationRequired === true,
  confirmationId: response?.confirmationId,
  materialActionTaken: response?.materialActionTaken === true,
  planChangePreview: response?.planChangePreview || null,
  trainingPlanDraft: response?.trainingPlanDraft || null,
  conversationRevisionAtTurn:
    typeof response?.conversationRevision === "number"
      ? response.conversationRevision
      : null,
});

export const AskYourCoachPanel = ({
  askCoach = askYourCoach,
  createSession = createCoachingSession,
  sendSessionMessage = sendCoachingSessionMessage,
  dismissSuggestion = dismissCoachingMemorySuggestion,
  onRequestTrainingIntake,
  onReviewTrainingPlanDraft = null,
}) => {
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [messages, setMessages] = useState([]);
  const conversationIdRef = useRef(null);
  const conversationRevisionRef = useRef(null);
  const [conversationId, setConversationId] = useState(null);
  const [conversationRevision, setConversationRevision] = useState(null);

  const appendMessage = useCallback((turn) => {
    setMessages((current) => [...current, turn]);
  }, []);

  const confirmCoachingAction = useCallback(async (confirmMessage) => {
    const clientMessageId = generateClientMessageId();
    const response = await askCoach({
      message: confirmMessage,
      conversationId: conversationIdRef.current || undefined,
      clientMessageId,
      expectedConversationRevision:
        typeof conversationRevisionRef.current === "number"
          ? conversationRevisionRef.current
          : undefined,
    });
    if (response?.conversationId) {
      conversationIdRef.current = response.conversationId;
      setConversationId(response.conversationId);
    }
    if (typeof response?.conversationRevision === "number") {
      conversationRevisionRef.current = response.conversationRevision;
      setConversationRevision(response.conversationRevision);
    }
    const intent = response?.coachingAction?.proposal?.intent;
    if (
      response?.materialActionTaken
      && affectsActiveTrainingPlan(intent, true)
    ) {
      notifyActivePlanChanged({ source: "coach-chat" });
    }
    return response;
  }, [askCoach]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    const trimmed = message.trim();
    if (!trimmed || loading) return;

    setError(null);
    setLoading(true);

    const athleteTurn = buildAthleteTurn(trimmed);
    appendMessage(athleteTurn);
    setMessage("");

    const clientMessageId = generateClientMessageId();
    const currentConversationId = conversationIdRef.current;
    const currentRevision = conversationRevisionRef.current;

    try {
      const response = await askCoach({
        message: trimmed,
        conversationId: currentConversationId || undefined,
        clientMessageId,
        expectedConversationRevision:
          typeof currentRevision === "number" ? currentRevision : undefined,
      });

      if (response?.conversationId) {
        conversationIdRef.current = response.conversationId;
        setConversationId(response.conversationId);
      }
      if (typeof response?.conversationRevision === "number") {
        conversationRevisionRef.current = response.conversationRevision;
        setConversationRevision(response.conversationRevision);
      }

      if (
        response?.status === "MODEL_UNAVAILABLE"
        && response?.retryable
        && !response?.reply
      ) {
        setError("AI Coach couldn't complete that response. Try again.");
        return;
      }

      appendMessage(buildCoachTurn(response));

      if (response?.requiresTrainingIntake && typeof onRequestTrainingIntake === "function") {
        onRequestTrainingIntake({
          mode: response.trainingIntakeMode || "new",
        });
      }
    } catch (err) {
      setError(
        err?.aiCoachError?.message
          || "Ask Your Coach is temporarily unavailable.",
      );
    } finally {
      setLoading(false);
    }
  };

  const conversationMeta = useMemo(
    () => ({ conversationId, conversationRevision }),
    [conversationId, conversationRevision],
  );

  return (
    <CoachingSectionCard
      title="Ask Your Coach"
      subtitle="Read-only coaching guidance"
      headingId="ask-your-coach-heading"
      testId="ask-your-coach-panel"
      elevated
    >
      {messages.length > 0 && (
        <div
          data-testid="ask-your-coach-transcript"
          data-conversation-id={conversationMeta.conversationId || ""}
          data-conversation-revision={
            conversationMeta.conversationRevision != null
              ? String(conversationMeta.conversationRevision)
              : ""
          }
          className="mb-4 space-y-3 max-w-3xl"
        >
          {messages.map((turn) => (
            <div
              key={turn.id}
              data-testid={
                turn.role === "athlete"
                  ? "ask-your-coach-athlete-turn"
                  : "ask-your-coach-answer"
              }
              data-turn-role={turn.role}
              className={
                turn.role === "athlete"
                  ? "app-card p-4 border-l-[3px] border-l-[var(--brand-primary)]"
                  : "app-card-elevated p-4 border-l-[3px] border-l-analytics-blue"
              }
            >
              <p className="card-label">
                {turn.role === "athlete" ? "You" : "Coach"}
              </p>
              <p className="mt-2 text-sm text-dark-text-primary whitespace-pre-wrap break-words leading-relaxed">
                {turn.message}
              </p>
              {turn.role === "coach" && turn.degradedMode && (
                <p
                  data-testid="ask-your-coach-degraded-notice"
                  className="mt-3 text-xs text-dark-text-muted"
                >
                  {DEGRADED_NOTICE}
                </p>
              )}
              {turn.role === "coach" && turn.memorySuggestion && (
                <MemorySuggestionCard
                  key={turn.id}
                  suggestion={turn.memorySuggestion}
                  onCreateSession={createSession}
                  onSendSessionMessage={sendSessionMessage}
                  onDismissSuggestion={dismissSuggestion}
                />
              )}
              {turn.role === "coach"
                && turn.coachingAction?.parseState === PROPOSAL_PARSE_STATE.VALID && (
                <CoachingActionCard
                  coachingAction={turn.coachingAction}
                  confirmationRequired={turn.confirmationRequired}
                  conversationId={conversationMeta.conversationId}
                  conversationRevision={turn.conversationRevisionAtTurn}
                  materialActionTaken={turn.materialActionTaken}
                  planChangePreview={turn.planChangePreview}
                  trainingPlanDraft={turn.trainingPlanDraft}
                  onReviewTrainingPlanDraft={onReviewTrainingPlanDraft}
                  onConfirm={confirmCoachingAction}
                  testId={`ask-coach-action-${turn.id}`}
                />
              )}
            </div>
          ))}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-4">
        <label htmlFor="ask-your-coach-message" className="card-label">
          Your question
        </label>
        <textarea
          id="ask-your-coach-message"
          data-testid="ask-your-coach-input"
          value={message}
          onChange={(event) => setMessage(event.target.value)}
          rows={3}
          className="app-control text-sm w-full"
          placeholder={
            messages.length > 0
              ? "Ask a follow-up…"
              : "Ask a coaching question…"
          }
          disabled={loading}
        />
        <Button
          type="submit"
          disabled={loading || !message.trim()}
          data-testid="ask-your-coach-submit"
          className="btn-primary-app"
        >
          {loading ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" />
              Asking…
            </>
          ) : (
            "Ask"
          )}
        </Button>
      </form>

      {error && (
        <p
          role="alert"
          data-testid="ask-your-coach-error"
          className="mt-4 text-sm text-analytics-amber"
        >
          {error}
        </p>
      )}
    </CoachingSectionCard>
  );
};
